import React, {useCallback, useEffect, useState} from 'react';
import dayjs from 'dayjs';
import {Button, Card, Form, Input, Modal, Select, Space, Table, Tag, Tooltip, message} from 'antd';
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    EyeOutlined,
    FileTextOutlined,
    PlusOutlined,
    SendOutlined,
} from '@/icons';
import {useAppStore} from '@/page/app/context';
import {useAuthStore} from '@/context/auth';
import {executePushOnce, getPushHistory, getSpecificPushes} from '@/api/hrPush';
import CreatePushModal from './CreatePushModal';
import SpecificPushDetailModal from './SpecificPushDetailModal';
import HistoryDetailModal from '../components/HistoryDetailModal';
import PushHistoryModal from '../components/PushHistoryModal';
import {getAuthorizedSpecificTypes, SPECIFIC_TYPES} from '../routineUtils';
import {formatApiTime} from '../utils';
import AppContent from '@/page/component/AppLayout/AppContent';

export default function SpecificPage() {
    const [{appId}] = useAppStore();
    const [authStore] = useAuthStore();
    const authorizedTypes = getAuthorizedSpecificTypes(authStore);
    const [form] = Form.useForm();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [detailTask, setDetailTask] = useState(null);
    const [historyTask, setHistoryTask] = useState(null);
    const [failureHistoryId, setFailureHistoryId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const {data} = await getSpecificPushes(appId, form.getFieldsValue());
            setRows(data || []);
        } finally {
            setLoading(false);
        }
    }, [appId, form]);

    useEffect(() => {
        if (appId) load();
    }, [appId, load]);

    const executeOnce = row => {
        Modal.confirm({
            title: '确认手动执行一次？',
            content: `将立即按当前配置执行「${row.taskName}」，并生成一条推送日志。`,
            onOk: async () => {
                try {
                    await executePushOnce(appId, row.id);
                    message.success('已触发手动执行');
                    load();
                } catch (e) {
                    // 失败原因（如"未到预定发送时间"）来自后端，必须显式提示，避免点了没反应
                    message.error(e?.message || '执行失败');
                }
            },
        });
    };

    const openFailureDetail = async row => {
        const {data} = await getPushHistory(appId, row.id, {current: 1, size: 1});
        const history = data?.records?.[0];
        if (!history) {
            message.warning('暂无推送日志');
            return;
        }
        setFailureHistoryId(history.id);
    };

    const columns = [
        {
            title: '状态',
            width: 110,
            render: (_, row) => {
                if (row.taskType === 'OFFBOARDING') {
                    if (row.approvalStatus === 0)
                        return (
                            <Tag icon={<ClockCircleOutlined />} color="orange">
                                待审批
                            </Tag>
                        );
                    if (row.approvalStatus === 2) return <Tag color="red">已驳回</Tag>;
                    if (row.errorMessage) {
                        return (
                            <Tooltip title={row.errorMessage}>
                                <Tag color="red">发送失败</Tag>
                            </Tooltip>
                        );
                    }
                    // 审批通过后未执行的（如未设置定时时间、尚未发送）显示"待发送"
                    return row.lastExecuteTime ? (
                        <Tag icon={<CheckCircleOutlined />} color="green">
                            发送
                        </Tag>
                    ) : (
                        <Tag icon={<ClockCircleOutlined />} color="blue">
                            待发送
                        </Tag>
                    );
                }
                return row.pushDate && !row.lastExecuteTime ? (
                    <Tag
                        icon={<ClockCircleOutlined />}
                        style={{
                            color: '#1677ff',
                            background: '#e6f4ff',
                            borderColor: '#91caff',
                        }}
                    >
                        定时
                    </Tag>
                ) : (
                    <Tag icon={<CheckCircleOutlined />} color="green">
                        发送
                    </Tag>
                );
            },
        },
        {title: '任务类型', dataIndex: 'taskType', render: value => SPECIFIC_TYPES[value] || value},
        {title: '消息模板', dataIndex: 'templateName'},
        {title: '发送人数', dataIndex: 'targetCount', width: 100},
        {title: '成功人数', dataIndex: 'successCount', width: 100},
        {
            title: '发送状态',
            width: 100,
            render: (_, row) => {
                if (row.taskType === 'OFFBOARDING') {
                    if (row.approvalStatus === 0) {
                        return (
                            <Tag icon={<ClockCircleOutlined />} color="orange">
                                待审批
                            </Tag>
                        );
                    }
                    if (row.approvalStatus === 2) {
                        return (
                            <Tooltip title={row.approvalOpinion || '无审批意见'}>
                                <Tag color="red">已驳回</Tag>
                            </Tooltip>
                        );
                    }
                }
                if (row.errorMessage) {
                    return (
                        <Tooltip title={row.errorMessage}>
                            <Tag color="red">发送失败</Tag>
                        </Tooltip>
                    );
                }
                if (!row.lastExecuteTime) {
                    return (
                        <Tag icon={<ClockCircleOutlined />} color="blue">
                            待发送
                        </Tag>
                    );
                }
                const hasFailure = row.targetCount > 0 && row.successCount < row.targetCount;

                return hasFailure ? (
                    <Button type="link" danger size="small" onClick={() => openFailureDetail(row)}>
                        ● 失败
                    </Button>
                ) : (
                    <Tag color="green">● 成功</Tag>
                );
            },
        },
        {
            title: '发送时间',
            dataIndex: 'lastExecuteTime',
            width: 170,
            render: formatApiTime,
        },
        {title: '操作人', dataIndex: 'creatorName', width: 120},
        {
            title: '操作',
            width: 270,
            render: (_, row) => (
                <Space>
                    <Button
                        type="link"
                        size="small"
                        icon={<SendOutlined />}
                        disabled={
                            (row.pushDate && !dayjs(row.pushDate).isSame(dayjs(), 'day')) ||
                            (row.taskType === 'OFFBOARDING' &&
                                (row.approvalStatus !== 1 ||
                                    (row.pushDate && !row.lastExecuteTime && !row.errorMessage)))
                        }
                        onClick={() => executeOnce(row)}
                    >
                        手动执行
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => setDetailTask(row)}
                    >
                        详情
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        icon={<FileTextOutlined />}
                        onClick={() => setHistoryTask(row)}
                    >
                        日志
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <AppContent>
            <Card
                title="特定消息推送"
                extra={
                    authorizedTypes.length > 0 ? (
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setOpen(true)}
                        >
                            新建特定推送
                        </Button>
                    ) : null
                }
            >
                <Form form={form} layout="inline" onFinish={load} style={{marginBottom: 16}}>
                    <Form.Item name="creator">
                        <Input placeholder="创建者" allowClear />
                    </Form.Item>
                    <Form.Item name="taskType">
                        <Select
                            placeholder="任务类型"
                            allowClear
                            style={{width: 150}}
                            options={Object.entries(SPECIFIC_TYPES)
                                .filter(([value]) => authorizedTypes.includes(value))
                                .map(([value, label]) => ({
                                    value,
                                    label,
                                }))}
                        />
                    </Form.Item>
                    <Space>
                        <Button type="primary" htmlType="submit">
                            搜索
                        </Button>
                        <Button
                            onClick={() => {
                                form.resetFields();
                                load();
                            }}
                        >
                            重置
                        </Button>
                    </Space>
                </Form>
                <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={rows}
                    loading={loading}
                    scroll={{x: 1200}}
                    pagination={{pageSize: 10, showTotal: total => `共 ${total} 条记录`}}
                />
                <CreatePushModal
                    open={open}
                    appId={appId}
                    onClose={() => setOpen(false)}
                    onSaved={() => {
                        setOpen(false);
                        load();
                    }}
                />
                {detailTask && (
                    <SpecificPushDetailModal
                        appId={appId}
                        task={detailTask}
                        onClose={() => setDetailTask(null)}
                    />
                )}
                {historyTask && (
                    <PushHistoryModal
                        appId={appId}
                        task={historyTask}
                        onClose={() => setHistoryTask(null)}
                    />
                )}
                {failureHistoryId && (
                    <HistoryDetailModal
                        appId={appId}
                        historyId={failureHistoryId}
                        taskName="特定推送"
                        onClose={() => setFailureHistoryId(null)}
                    />
                )}
            </Card>
        </AppContent>
    );
}
